import { belongsTo, Factory, Model } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import type { MirageServer, MockServer } from '@@/api/mock-server/server'
import { rand, randNumber, randPastDate } from '@ngneat/falso'
import type { SchemaAttributes } from '@@/api/types'
import {
  filterable,
  pageable,
  PaginationError,
  createErrorResponse,
} from './utils.js'
import { generateFinanceAmount } from '@@/api/mock-server/utils.js'
import { roundEven, toNumber } from '@/utils/number.js'

type CreditAllocationData = SchemaAttributes<'CreditAllocation'>

export const model = Model.extend({
  client: belongsTo('client'),
  invoice: belongsTo('invoice'),
  department: belongsTo('department'),
}) as ModelDefinition<CreditAllocationData>

export const factory = Factory.extend<CreditAllocationData>({
  type_code() {
    return randNumber({ min: 1, max: 2 }) as CreditAllocationData['type_code']
  },
  type_code_display() {
    return this.type_code === 1
      ? 'Norion Bank AB / Walley invoicing'
      : 'Invoice gateway'
  },
  status() {
    return randNumber({ min: 1, max: 10 }) as CreditAllocationData['status']
  },
  status_display() {
    const statusMap: Record<number, string> = {
      1: 'Created',
      2: 'Application sent',
      3: 'Approved',
      4: 'Payment pending',
      5: 'Fully used',
      6: 'Failed',
      7: 'Rejected',
      8: 'Cancelled',
      9: 'Expired',
      10: 'Checkout link sent',
    }
    return statusMap[this.status as number] ?? 'Unknown'
  },
  amount() {
    return roundEven(
      toNumber(generateFinanceAmount({ min: 100, max: 5000 })),
      2,
    )
  },
  unused_amount() {
    return roundEven(
      rand([
        0,
        toNumber(generateFinanceAmount({ min: 0, max: toNumber(this.amount) })),
        toNumber(this.amount),
      ]),
      2,
    )
  },
  created() {
    return randPastDate().toISOString()
  },
  modified() {
    return randPastDate().toISOString()
  },
  expiration_datetime() {
    return null
  },
  is_expired() {
    return false
  },
})

export function routes(server: MockServer) {
  // GET /creditallocation - List credit allocations
  server.get(
    '/creditallocation',
    function (this: MirageServer, schema, request) {
      const creditAllocations = schema.all('creditAllocation')

      try {
        const filtered = filterable(creditAllocations, request, {
          invoice_id: (allocation, value) => {
            return String(allocation.invoiceId) === String(value)
          },
          client_id: (allocation, value) => {
            return String(allocation.clientId) === String(value)
          },
        })

        const { data, meta } = pageable(filtered, request)

        return {
          ...this.serialize(data),
          meta: {
            pagination: meta,
          },
        }
      } catch (e) {
        if (e instanceof PaginationError) {
          return createErrorResponse({
            status: 404,
            errors: [{ detail: e.message }],
          })
        }
        return createErrorResponse({
          status: 500,
          errors: [{ detail: 'Internal Server Error' }],
        })
      }
    },
  )

  // GET /creditallocation/:id - Retrieve specific credit allocation
  server.get(
    '/creditallocation/:id',
    function (this: MirageServer, schema, request) {
      const { id } = request.params
      const creditAllocation = schema.find('creditAllocation', id)

      if (!creditAllocation) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: `Credit allocation with id ${id} not found` }],
        })
      }

      return creditAllocation
    },
  )
}
